import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import FormField, { PosInput, PosSelect } from '../components/pos/FormField';
import { getActiveSchoolId } from '../utils/activeSchool';
import { listShifts } from '../services/cashShiftApi';
import {
    listWebhooks, saveWebhook, deleteWebhook, testWebhook, webhookLogs,
    integrationEvents, downloadExport, listExportTemplates, listExportJobs,
    saveExportJob, runExportJob, testFtp, listAllWebhookLogs, retryWebhookLog
} from '../services/integrationApi';

import { formatDateTime } from '../utils/dateFormat';

const fmtDt = (s) => s ? formatDateTime(s) : '—';

export default function SchoolIntegrationsPage() {
    const { t } = useTranslation(['schoolIntegrations', 'common', 'nav', 'messages', 'errors']);
    const eventGroups = useMemo(() => [
        { title: t('schoolIntegrations:eventGroupCash'), codes: ['receipt.created', 'receipt.refunded', 'shift.opened', 'shift.closed', 'cash.deposit', 'cash.withdrawal'] },
        { title: t('schoolIntegrations:eventGroupProducts'), codes: ['product.created', 'product.updated', 'stock.low'] },
        { title: t('schoolIntegrations:eventGroupClients'), codes: ['client.created', 'bonus.accrued', 'bonus.redeemed'] }
    ], [t]);
    const schoolId = getActiveSchoolId();
    const [tab, setTab] = useState('webhooks');
    const [hooks, setHooks] = useState([]);
    const [events, setEvents] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [shifts, setShifts] = useState([]);
    const [history, setHistory] = useState([]);
    const [historyFilter, setHistoryFilter] = useState({ webhookId: '', status: '' });
    const [hookWizard, setHookWizard] = useState(null);
    const [hookStep, setHookStep] = useState(0);
    const [hookForm, setHookForm] = useState({
        id: null, name: '', url: '', format: 'json', authType: 'none',
        authCredentials: '', authHeaderName: 'X-Api-Key', events: ['receipt.created', 'shift.closed'], isActive: true
    });
    const [exportForm, setExportForm] = useState({
        kind: 'receipts', from: '', to: '', format: 'csv', shiftId: '', groupBy: 'day'
    });
    const [jobWizard, setJobWizard] = useState(false);
    const [jobStep, setJobStep] = useState(0);
    const [jobForm, setJobForm] = useState({
        name: '', templateId: 1, scheduleType: 'daily', scheduleTime: '03:00',
        dateRangeType: 'yesterday', transportType: 'sftp', host: '', port: 22,
        username: '', password: '', remotePath: '/exchange/', fileNamePattern: 'receipts_{DateFrom}_{DateTo}.xml',
        skipIfEmpty: true
    });
    const [msg, setMsg] = useState('');
    const [logDetail, setLogDetail] = useState(null);

    const eventMap = useMemo(() => {
        const m = {};
        (events || []).forEach(e => { m[e.code] = e; });
        return m;
    }, [events]);

    useEffect(() => { if (schoolId) load(); }, [schoolId, tab]);

    async function load() {
        if (tab === 'webhooks' || tab === 'history') {
            const [h, ev] = await Promise.all([listWebhooks(schoolId), integrationEvents()]);
            setHooks(h.data || []);
            setEvents(ev.data || []);
        }
        if (tab === 'history') await loadHistory();
        if (tab === 'export' || tab === 'scheduled') {
            const t = await listExportTemplates(schoolId);
            setTemplates(t.data || []);
        }
        if (tab === 'export') {
            const orgId = hooks[0]?.organizationId;
            if (schoolId) {
                try {
                    const s = await listShifts(schoolId, orgId || 0);
                    setShifts((s.data || []).filter(x => x.status === 'closed'));
                } catch { setShifts([]); }
            }
        }
        if (tab === 'scheduled') {
            const j = await listExportJobs(schoolId);
            setJobs(j.data || []);
        }
    }

    async function loadHistory() {
        const res = await listAllWebhookLogs(schoolId, {
            webhookId: historyFilter.webhookId || undefined,
            status: historyFilter.status || undefined
        });
        setHistory(res.data?.items || []);
    }

    function openHookWizard(edit) {
        if (edit) {
            let ev = [];
            try { ev = JSON.parse(edit.events || '[]'); } catch { ev = []; }
            setHookForm({
                id: edit.id, name: edit.name, url: edit.url, format: edit.format,
                authType: edit.authType || 'none', authCredentials: '', authHeaderName: edit.authHeaderName || 'X-Api-Key',
                events: ev, isActive: edit.isActive
            });
        } else {
            setHookForm({
                id: null, name: '', url: '', format: 'json', authType: 'none', authCredentials: '',
                authHeaderName: 'X-Api-Key', events: ['receipt.created', 'shift.closed'], isActive: true
            });
        }
        setHookStep(0);
        setHookWizard(true);
    }

    async function saveHook() {
        await saveWebhook({ ...hookForm, schoolId }, hookForm.id);
        setHookWizard(null);
        setMsg(t('schoolIntegrations:msgSaved'));
        load();
    }

    async function doExport() {
        const params = { schoolId, format: exportForm.format };
        let path = '/integration/export/receipts';
        if (exportForm.kind === 'products') path = '/integration/export/products';
        else if (exportForm.kind === 'sales') {
            path = '/integration/export/sales-report';
            params.from = exportForm.from;
            params.to = exportForm.to;
            params.groupBy = exportForm.groupBy;
        } else if (exportForm.kind === 'shift') {
            path = `/integration/export/shift-report/${exportForm.shiftId}`;
        } else {
            params.from = exportForm.from;
            params.to = exportForm.to;
        }
        const res = await downloadExport(path, params);
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `export.${exportForm.format === 'csv' ? 'csv' : exportForm.format}`;
        a.click();
        setMsg(t('schoolIntegrations:msgFileDownloaded'));
    }

    function setQuickPeriod(preset) {
        const today = new Date();
        const to = today.toISOString().slice(0, 10);
        let from = to;
        if (preset === 'week') {
            const d = new Date(today);
            d.setDate(d.getDate() - 7);
            from = d.toISOString().slice(0, 10);
        } else if (preset === 'month') {
            const d = new Date(today.getFullYear(), today.getMonth(), 1);
            from = d.toISOString().slice(0, 10);
        }
        setExportForm(f => ({ ...f, from, to }));
    }

    async function saveJob() {
        await saveExportJob({ ...jobForm, schoolId });
        setJobWizard(false);
        setMsg(t('schoolIntegrations:msgJobSaved'));
        load();
    }

    return (
        <OwnerPageLayout
            title={t('schoolIntegrations:title')}
            subtitle={t('schoolIntegrations:subtitle')}
            className="pos-page pos-ui"
            maxWidth={920}
            actions={<Link to="/school/settings" className="pos-btn sp-btn--ghost">{t('schoolIntegrations:backToSettings')}</Link>}
        >
            <div style={{ paddingBottom: 40 }}>
                {msg && <div className="pos-card" style={{ background: '#ecfdf5', marginBottom: 16 }}>{msg}</div>}

                <div className="pos-tabs">
                    <button type="button" className={tab === 'webhooks' ? 'active' : ''} onClick={() => setTab('webhooks')}>{t('schoolIntegrations:tabs.webhook')}</button>
                    <button type="button" className={tab === 'export' ? 'active' : ''} onClick={() => setTab('export')}>{t('schoolIntegrations:tabs.export')}</button>
                    <button type="button" className={tab === 'scheduled' ? 'active' : ''} onClick={() => setTab('scheduled')}>{t('schoolIntegrations:tabs.scheduled')}</button>
                    <button type="button" className={tab === 'history' ? 'active' : ''} onClick={() => setTab('history')}>{t('schoolIntegrations:tabs.history')}</button>
                </div>

                {tab === 'webhooks' && (
                    <>
                        <button type="button" className="pos-btn pos-btn--primary" style={{ marginBottom: 16 }}
                            onClick={() => openHookWizard(null)}>{t('schoolIntegrations:addIntegration')}</button>
                        {hooks.map(h => {
                            let ev = [];
                            try { ev = JSON.parse(h.events || '[]'); } catch { ev = []; }
                            return (
                                <div key={h.id} className="pos-card" style={{ marginBottom: 12 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                                        <div>
                                            <strong>📡 {h.name}</strong>
                                            <span style={{ marginLeft: 8, fontSize: 12, color: h.isActive ? '#15803d' : '#94a3b8' }}>
                                                {h.isActive ? t('schoolIntegrations:statusActive') : t('schoolIntegrations:statusInactive')}
                                            </span>
                                            <p style={{ fontSize: 13, color: 'var(--pos-muted)', margin: '6px 0 0', wordBreak: 'break-all' }}>{h.url}</p>
                                            <p style={{ fontSize: 13, margin: '4px 0 0' }}>
                                                {t('schoolIntegrations:hookMeta', { count: ev.length, format: h.format === 'xml' ? t('schoolIntegrations:formatXml') : h.format.toUpperCase() })}
                                            </p>
                                            <p style={{ fontSize: 13, color: 'var(--pos-muted)' }}>
                                                {t('schoolIntegrations:lastSend', { date: fmtDt(h.lastTriggeredAt), status: h.lastStatus === 'success' ? '✓' : h.lastStatus || '' })}
                                            </p>
                                        </div>
                                        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                                            <button type="button" className="pos-btn pos-btn--ghost" onClick={() => openHookWizard(h)}>{t('common:edit')}</button>
                                            <button type="button" className="pos-btn pos-btn--soft"
                                                onClick={() => testWebhook(h.id, schoolId).then(() => setMsg(t('schoolIntegrations:msgTestSent')))}>{t('schoolIntegrations:test')}</button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        {!hooks.length && <p style={{ color: 'var(--pos-muted)' }}>{t('schoolIntegrations:noHooks')}</p>}
                    </>
                )}

                {tab === 'export' && (
                    <div className="pos-card">
                        <h3 style={{ marginTop: 0 }}>{t('schoolIntegrations:exportTitle')}</h3>
                        <p style={{ color: 'var(--pos-muted)', fontSize: 14 }}>{t('schoolIntegrations:exportHint')}</p>
                        <FormField label={t('schoolIntegrations:exportKind')}>
                            <PosSelect value={exportForm.kind} onChange={e => setExportForm({ ...exportForm, kind: e.target.value })}>
                                <option value="receipts">{t('schoolIntegrations:kindReceipts')}</option>
                                <option value="products">{t('schoolIntegrations:kindProducts')}</option>
                                <option value="sales">{t('schoolIntegrations:kindSales')}</option>
                                <option value="shift">{t('schoolIntegrations:kindShift')}</option>
                            </PosSelect>
                        </FormField>
                        {exportForm.kind !== 'products' && exportForm.kind !== 'shift' && (
                            <>
                                <FormField label={t('schoolIntegrations:periodFrom')}><PosInput type="date" value={exportForm.from} onChange={e => setExportForm({ ...exportForm, from: e.target.value })} /></FormField>
                                <FormField label={t('schoolIntegrations:periodTo')}><PosInput type="date" value={exportForm.to} onChange={e => setExportForm({ ...exportForm, to: e.target.value })} /></FormField>
                                <div className="pos-quick-chips">
                                    <button type="button" onClick={() => setQuickPeriod('today')}>{t('schoolIntegrations:today')}</button>
                                    <button type="button" onClick={() => setQuickPeriod('week')}>{t('schoolIntegrations:week')}</button>
                                    <button type="button" onClick={() => setQuickPeriod('month')}>{t('schoolIntegrations:month')}</button>
                                </div>
                            </>
                        )}
                        {exportForm.kind === 'shift' && (
                            <FormField label={t('schoolIntegrations:shift')}>
                                <PosSelect value={exportForm.shiftId} onChange={e => setExportForm({ ...exportForm, shiftId: e.target.value })}>
                                    <option value="">{t('schoolIntegrations:pickShift')}</option>
                                    {shifts.map(s => (
                                        <option key={s.id} value={s.id}>№{s.shiftNumber} · {fmtDt(s.closedAt || s.openedAt)}</option>
                                    ))}
                                </PosSelect>
                            </FormField>
                        )}
                        {exportForm.kind === 'sales' && (
                            <FormField label={t('schoolIntegrations:groupBy')}>
                                <PosSelect value={exportForm.groupBy} onChange={e => setExportForm({ ...exportForm, groupBy: e.target.value })}>
                                    <option value="day">{t('schoolIntegrations:byDay')}</option>
                                    <option value="week">{t('schoolIntegrations:byWeek')}</option>
                                    <option value="month">{t('schoolIntegrations:byMonth')}</option>
                                </PosSelect>
                            </FormField>
                        )}
                        <FormField label={t('schoolIntegrations:format')}>
                            <div className="pos-quick-chips">
                                {['csv', 'xml', 'json'].map(f => (
                                    <button key={f} type="button"
                                        className={exportForm.format === f ? 'active' : ''}
                                        style={exportForm.format === f ? { background: 'var(--pos-accent)', color: '#fff' } : {}}
                                        onClick={() => setExportForm({ ...exportForm, format: f })}>
                                        {f === 'csv' ? t('schoolIntegrations:formatCsv') : f === 'xml' ? t('schoolIntegrations:formatXml1c') : t('schoolIntegrations:formatJson')}
                                    </button>
                                ))}
                            </div>
                        </FormField>
                        <button type="button" className="pos-btn pos-btn--primary" onClick={doExport}>{t('schoolIntegrations:downloadFile')}</button>
                    </div>
                )}

                {tab === 'scheduled' && (
                    <>
                        <button type="button" className="pos-btn pos-btn--primary" style={{ marginBottom: 16 }}
                            onClick={() => { setJobStep(0); setJobWizard(true); }}>{t('schoolIntegrations:addScheduledJob')}</button>
                        {jobs.map(j => (
                            <div key={j.id} className="pos-card" style={{ marginBottom: 12 }}>
                                <strong>📤 {j.name}</strong>
                                <span style={{ marginLeft: 8, fontSize: 12, color: j.isActive ? '#15803d' : '#94a3b8' }}>{j.isActive ? t('schoolIntegrations:jobActive') : t('schoolIntegrations:jobInactive')}</span>
                                <p style={{ fontSize: 13, margin: '8px 0 0' }}>{t('schoolIntegrations:jobTemplate', { name: j.templateName, schedule: j.scheduleType, time: j.scheduleTime })}</p>
                                <p style={{ fontSize: 13, color: 'var(--pos-muted)' }}>{t('schoolIntegrations:jobSftp', { host: j.host, path: j.remotePath })}</p>
                                <p style={{ fontSize: 13 }}>{t('schoolIntegrations:jobRuns', { last: j.lastRunStatus || '—', next: fmtDt(j.nextRunAt) })}</p>
                                <button type="button" className="pos-btn pos-btn--soft" style={{ marginTop: 8 }}
                                    onClick={() => runExportJob(j.id, schoolId).then(() => setMsg(t('schoolIntegrations:msgJobStarted')))}>{t('schoolIntegrations:runNow')}</button>
                            </div>
                        ))}
                    </>
                )}

                {tab === 'history' && (
                    <>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                            <PosSelect value={historyFilter.webhookId} onChange={e => setHistoryFilter({ ...historyFilter, webhookId: e.target.value })}>
                                <option value="">{t('schoolIntegrations:filterAllHooks')}</option>
                                {hooks.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                            </PosSelect>
                            <PosSelect value={historyFilter.status} onChange={e => setHistoryFilter({ ...historyFilter, status: e.target.value })}>
                                <option value="">{t('schoolIntegrations:filterAllStatuses')}</option>
                                <option value="ok">{t('schoolIntegrations:statusOk')}</option>
                                <option value="error">{t('schoolIntegrations:statusError')}</option>
                            </PosSelect>
                            <button type="button" className="pos-btn pos-btn--ghost" onClick={loadHistory}>{t('schoolIntegrations:refresh')}</button>
                        </div>
                        <table className="pos-table">
                            <thead>
                                <tr><th>{t('schoolIntegrations:thDate')}</th><th>{t('schoolIntegrations:thIntegration')}</th><th>{t('schoolIntegrations:thEvent')}</th><th>{t('schoolIntegrations:thStatus')}</th><th>{t('schoolIntegrations:thCode')}</th><th>{t('schoolIntegrations:thMs')}</th><th></th></tr>
                            </thead>
                            <tbody>
                                {history.map(l => (
                                    <tr key={l.id}>
                                        <td>{fmtDt(l.sentAt)}</td>
                                        <td>{l.webhookName}</td>
                                        <td>{l.eventType}</td>
                                        <td>{l.success ? '✓' : '✗'}</td>
                                        <td>{l.responseStatus ?? '—'}</td>
                                        <td>{l.durationMs ?? '—'}</td>
                                        <td>
                                            {!l.success && (
                                                <button type="button" className="pos-btn pos-btn--ghost" style={{ padding: '4px 8px' }}
                                                    onClick={() => retryWebhookLog(l.id, schoolId).then(() => { setMsg(t('schoolIntegrations:msgRetrySent')); loadHistory(); })}>
                                                    {t('schoolIntegrations:retry')}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}
            </div>

            {hookWizard && (
                <div className="pos-modal-backdrop" onClick={() => setHookWizard(null)}>
                    <div className="pos-modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
                        <h3>{hookForm.id ? t('schoolIntegrations:hookEditTitle') : t('schoolIntegrations:hookNewTitle')}</h3>
                        <p style={{ color: 'var(--pos-muted)', fontSize: 13 }}>{t('schoolIntegrations:stepOf', { current: hookStep + 1, total: 4 })}</p>
                        {hookStep === 0 && (
                            <>
                                <FormField label={t('schoolIntegrations:name')}><PosInput value={hookForm.name} onChange={e => setHookForm({ ...hookForm, name: e.target.value })} placeholder={t('schoolIntegrations:namePlaceholder')} /></FormField>
                                <FormField label={t('schoolIntegrations:url')}><PosInput value={hookForm.url} onChange={e => setHookForm({ ...hookForm, url: e.target.value })} placeholder="https://..." /></FormField>
                                <FormField label={t('schoolIntegrations:format')}>
                                    <label className="pos-check"><input type="radio" checked={hookForm.format === 'json'} onChange={() => setHookForm({ ...hookForm, format: 'json' })} /> {t('schoolIntegrations:formatJsonUniversal')}</label>
                                    <label className="pos-check"><input type="radio" checked={hookForm.format === 'xml'} onChange={() => setHookForm({ ...hookForm, format: 'xml' })} /> {t('schoolIntegrations:formatXml1cFull')}</label>
                                    <label className="pos-check"><input type="radio" checked={hookForm.format === 'csv'} onChange={() => setHookForm({ ...hookForm, format: 'csv' })} /> {t('schoolIntegrations:formatCsvExcel')}</label>
                                </FormField>
                            </>
                        )}
                        {hookStep === 1 && (
                            <>
                                <label className="pos-check"><input type="radio" checked={hookForm.authType === 'none'} onChange={() => setHookForm({ ...hookForm, authType: 'none' })} /> {t('schoolIntegrations:authNone')}</label>
                                <label className="pos-check"><input type="radio" checked={hookForm.authType === 'bearer'} onChange={() => setHookForm({ ...hookForm, authType: 'bearer' })} /> {t('schoolIntegrations:authBearer')}</label>
                                {hookForm.authType === 'bearer' && (
                                    <FormField label={t('schoolIntegrations:token')}><PosInput type="password" value={hookForm.authCredentials} onChange={e => setHookForm({ ...hookForm, authCredentials: e.target.value })} /></FormField>
                                )}
                                <label className="pos-check"><input type="radio" checked={hookForm.authType === 'basic'} onChange={() => setHookForm({ ...hookForm, authType: 'basic' })} /> {t('schoolIntegrations:authBasic')}</label>
                                {hookForm.authType === 'basic' && (
                                    <FormField label={t('schoolIntegrations:loginPassword')}><PosInput type="password" value={hookForm.authCredentials} onChange={e => setHookForm({ ...hookForm, authCredentials: e.target.value })} /></FormField>
                                )}
                                <label className="pos-check"><input type="radio" checked={hookForm.authType === 'apikey'} onChange={() => setHookForm({ ...hookForm, authType: 'apikey' })} /> {t('schoolIntegrations:authApiKey')}</label>
                                {hookForm.authType === 'apikey' && (
                                    <>
                                        <FormField label={t('schoolIntegrations:headerName')}><PosInput value={hookForm.authHeaderName} onChange={e => setHookForm({ ...hookForm, authHeaderName: e.target.value })} /></FormField>
                                        <FormField label={t('schoolIntegrations:headerValue')}><PosInput type="password" value={hookForm.authCredentials} onChange={e => setHookForm({ ...hookForm, authCredentials: e.target.value })} /></FormField>
                                    </>
                                )}
                            </>
                        )}
                        {hookStep === 2 && eventGroups.map(g => (
                            <div key={g.title} style={{ marginBottom: 12 }}>
                                <p style={{ fontWeight: 600, margin: '0 0 6px' }}>{g.title}</p>
                                {g.codes.map(code => (
                                    <label key={code} className="pos-check">
                                        <input type="checkbox" checked={hookForm.events.includes(code)}
                                            onChange={e => {
                                                const evs = e.target.checked
                                                    ? [...hookForm.events, code]
                                                    : hookForm.events.filter(x => x !== code);
                                                setHookForm({ ...hookForm, events: evs });
                                            }} />
                                        {eventMap[code]?.title || code}
                                    </label>
                                ))}
                            </div>
                        ))}
                        {hookStep === 3 && (
                            <p>{t('schoolIntegrations:hookReview')}</p>
                        )}
                        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                            {hookStep > 0 && <button type="button" className="pos-btn pos-btn--ghost" onClick={() => setHookStep(hookStep - 1)}>{t('common:back')}</button>}
                            {hookStep < 3 && <button type="button" className="pos-btn pos-btn--primary" onClick={() => setHookStep(hookStep + 1)}>{t('common:next')}</button>}
                            {hookStep === 3 && <button type="button" className="pos-btn pos-btn--primary" onClick={saveHook}>{t('common:save')}</button>}
                            <button type="button" className="pos-btn pos-btn--ghost" onClick={() => setHookWizard(null)}>{t('common:cancel')}</button>
                        </div>
                    </div>
                </div>
            )}

            {jobWizard && (
                <div className="pos-modal-backdrop" onClick={() => setJobWizard(false)}>
                    <div className="pos-modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
                        <h3>{t('schoolIntegrations:jobTitle', { step: jobStep + 1 })}</h3>
                        {jobStep === 0 && (
                            <>
                                <FormField label={t('schoolIntegrations:name')}><PosInput value={jobForm.name} onChange={e => setJobForm({ ...jobForm, name: e.target.value })} /></FormField>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                    {templates.map(tpl => (
                                        <button key={tpl.id} type="button" className={`pos-pay-tile${jobForm.templateId === tpl.id ? ' pos-pay-tile--active' : ''}`}
                                            onClick={() => setJobForm({ ...jobForm, templateId: tpl.id })}>
                                            <span className="pos-pay-tile__label">{tpl.name}</span>
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                        {jobStep === 1 && (
                            <>
                                <FormField label={t('nav:schedule')}>
                                    <PosSelect value={jobForm.scheduleType} onChange={e => setJobForm({ ...jobForm, scheduleType: e.target.value })}>
                                        <option value="hourly">{t('schoolIntegrations:scheduleHourly')}</option>
                                        <option value="daily">{t('schoolIntegrations:scheduleDaily')}</option>
                                        <option value="weekly">{t('schoolIntegrations:scheduleWeekly')}</option>
                                    </PosSelect>
                                </FormField>
                                <FormField label={t('schoolIntegrations:time')}><PosInput value={jobForm.scheduleTime} onChange={e => setJobForm({ ...jobForm, scheduleTime: e.target.value })} /></FormField>
                                <FormField label={t('schoolIntegrations:dataPeriod')}>
                                    <PosSelect value={jobForm.dateRangeType} onChange={e => setJobForm({ ...jobForm, dateRangeType: e.target.value })}>
                                        <option value="yesterday">{t('schoolIntegrations:yesterday')}</option>
                                        <option value="today">{t('schoolIntegrations:today')}</option>
                                        <option value="last_week">{t('schoolIntegrations:lastWeek')}</option>
                                        <option value="last_month">{t('schoolIntegrations:lastMonth')}</option>
                                    </PosSelect>
                                </FormField>
                                <label className="pos-check">
                                    <input type="checkbox" checked={jobForm.skipIfEmpty} onChange={e => setJobForm({ ...jobForm, skipIfEmpty: e.target.checked })} />
                                    {t('schoolIntegrations:skipEmpty')}
                                </label>
                            </>
                        )}
                        {jobStep === 2 && (
                            <>
                                <FormField label={t('schoolIntegrations:protocol')}>
                                    <PosSelect value={jobForm.transportType} onChange={e => setJobForm({ ...jobForm, transportType: e.target.value })}>
                                        <option value="ftp">FTP</option>
                                        <option value="sftp">SFTP</option>
                                        <option value="ftps">FTPS</option>
                                    </PosSelect>
                                </FormField>
                                <FormField label={t('schoolIntegrations:server')}><PosInput value={jobForm.host} onChange={e => setJobForm({ ...jobForm, host: e.target.value })} /></FormField>
                                <FormField label={t('schoolIntegrations:port')}><PosInput type="number" value={jobForm.port} onChange={e => setJobForm({ ...jobForm, port: parseInt(e.target.value, 10) })} /></FormField>
                                <FormField label={t('schoolIntegrations:login')}><PosInput value={jobForm.username} onChange={e => setJobForm({ ...jobForm, username: e.target.value })} /></FormField>
                                <FormField label={t('schoolIntegrations:password')}><PosInput type="password" value={jobForm.password} onChange={e => setJobForm({ ...jobForm, password: e.target.value })} /></FormField>
                                <FormField label={t('schoolIntegrations:remotePath')}><PosInput value={jobForm.remotePath} onChange={e => setJobForm({ ...jobForm, remotePath: e.target.value })} /></FormField>
                                <button type="button" className="pos-btn pos-btn--ghost" onClick={() => testFtp({ ...jobForm, schoolId }).then(r => setMsg(r.data?.success ? t('schoolIntegrations:msgFtpOk') : r.data?.errorMessage))}>
                                    {t('schoolIntegrations:testConnection')}
                                </button>
                            </>
                        )}
                        {jobStep === 3 && <p>{t('schoolIntegrations:saveJobConfirm', { name: jobForm.name })}</p>}
                        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                            {jobStep > 0 && <button type="button" className="pos-btn pos-btn--ghost" onClick={() => setJobStep(jobStep - 1)}>{t('common:back')}</button>}
                            {jobStep < 3 && <button type="button" className="pos-btn pos-btn--primary" onClick={() => setJobStep(jobStep + 1)}>{t('common:next')}</button>}
                            {jobStep === 3 && <button type="button" className="pos-btn pos-btn--primary" onClick={saveJob}>{t('common:save')}</button>}
                        </div>
                    </div>
                </div>
            )}
        </OwnerPageLayout>
    );
}
